import React, { useState, useCallback, useMemo } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { GlobalPropertySearch } from './global-property-search';
import { AggregationSection } from '../../../../types';
import FilterSection, {
  isValidSection,
  findUngroupedSections,
  findGroupedSections,
} from '../filter-section';
import * as S from './styles';
import FilterSectionGroup from './group';
import { ProductCollectionNonNameSearchFilter } from './types';

type Props = {
  sections: AggregationSection[];
  filters: ProductCollectionNonNameSearchFilter[];
  isSearching: boolean;
  hasSectionMatchingFilter: GlobalPropertySearch['hasSectionMatchingFilter'];
  filterByPropertySearch: GlobalPropertySearch['filterByPropertySearch'];
};
// !!This component is used both on desktop and mobile property search!!
export const AllFilterSections = ({
  sections,
  filters,
  isSearching,
  filterByPropertySearch,
  hasSectionMatchingFilter,
}: Props) => {
  const { t, market } = React.useContext(AppContext);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const toggleExpandedGroup = useCallback(
    (id: string) => {
      if (expandedGroups.includes(id)) {
        setExpandedGroups(expandedGroups.filter(groupId => groupId !== id));
      } else {
        setExpandedGroups([...expandedGroups, id]);
      }
    },
    [expandedGroups]
  );

  const ungroupedSections = useMemo(() => findUngroupedSections(sections), [
    sections,
  ]);

  const sectionGroups = useMemo(
    () => findGroupedSections(sections, filters, market.code),
    [sections, filters, market.code]
  );

  return (
    <>
      {sections && sections.some(isValidSection) ? (
        <>
          {ungroupedSections.length > 0 && (
            <S.SectionList>
              {ungroupedSections.filter(filterByPropertySearch).map(section => (
                <S.SectionListItem key={section.id}>
                  <FilterSection
                    section={section}
                    showTitle
                    disableSearch={isSearching}
                  />
                </S.SectionListItem>
              ))}
            </S.SectionList>
          )}
          {sectionGroups.length > 0 && (
            <S.SectionList>
              {sectionGroups.filter(hasSectionMatchingFilter).map(group => (
                <S.SectionListItem noPadding key={group.title}>
                  <FilterSectionGroup
                    group={group}
                    expanded={
                      expandedGroups.includes(group.title) || isSearching
                    }
                    toggleExpanded={toggleExpandedGroup}
                    dark
                  >
                    <S.SectionList>
                      {group.sections
                        .filter(filterByPropertySearch)
                        .map(section => (
                          <S.SectionListItem key={section.id}>
                            <FilterSection
                              section={section}
                              showTitle
                              disableSearch={isSearching}
                            />
                          </S.SectionListItem>
                        ))}
                    </S.SectionList>
                  </FilterSectionGroup>
                </S.SectionListItem>
              ))}
            </S.SectionList>
          )}
        </>
      ) : (
        <S.NoContent>{t('filter', 'filter.no-advanced-filters')}</S.NoContent>
      )}
    </>
  );
};
